import {londonDateTimeInputInstant} from '../dates';
import { formatDateOnly } from "../dates";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api";
import { alphaSave } from "../ui/alpha-save";
import { ErrorState, Loading } from "../ui/State";
type Member = {
  personId: string;
  entitlement: string;
  votes: number;
  present: boolean;
  proxyPersonId: string | null;
  proxyEvidence: string;
};
type Resolution = {
  title: string;
  text: string;
  actionTitle: string;
  ballots: { personId: string; vote: "For" | "Against" | "Abstain" }[];
};
type Meeting = {
  event_id: string;
  title: string;
  version: number;
  stage: string;
  register: Member[];
  resolutions: Resolution[];
  minutes: string;
  notice: string;
  rules_reference: string;
  quorum: number;
  quorum_basis: string;
  majority_percent: number;
};
type Model = {
  items: Meeting[];
  packs: { id: string; report_kind: string; period_start: string }[];
  people: { id: string; display_name: string }[];
};
export function MeetingsAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [model, setModel] = useState<Model>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [selected, setSelected] = useState<Meeting>(),
    [members, setMembers] = useState<Member[]>([]),
    [resolutions, setResolutions] = useState<Resolution[]>([]),
    [minutes, setMinutes] = useState("");
  const pending = useRef<{ url: string; body: object } | null>(null);
  const load = () =>
    api<Model>("/api/alpha/meetings?building=" + building).then(setModel);
  useEffect(() => {
    setModel(undefined);
    setSelected(undefined);
    if (building) void load().catch(setError);
  }, [building]);
  function edit(m: Meeting) {
    setSelected(m);
    setMembers(m.register);
    setResolutions(
      m.resolutions.map((r) => ({
        title: r.title,
        text: r.text,
        actionTitle: r.actionTitle,
        ballots: r.ballots,
      })),
    );
    setMinutes(m.minutes ?? "");
  }
  async function write(url: string, body: object) {
    setBusy(true);
    setError(undefined);
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    try {
      await alphaSave(pending, pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      await load();
      setSelected(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void write("/api/alpha/meetings", {
      building,
      packId: f.get("pack"),
      title: f.get("title"),
      startsAt: londonDateTimeInputInstant(String(f.get("when"))),
      notice: f.get("notice"),
      rulesReference: f.get("rules"),
      quorum: Number(f.get("quorum")),
      quorumBasis: f.get("basis"),
      majorityPercent: Number(f.get("majority")),
    });
  }
  const name = (id: string) =>
    model?.people.find((p) => p.id === id)?.display_name ??
    "Member no longer available";
  const updateMember = (i: number, patch: Partial<Member>) =>
    setMembers((ms) => ms.map((m, n) => (n === i ? { ...m, ...patch } : m)));
  return (
    <>
      <h1>AGM meetings and resolutions</h1>
      <p>
        Prepare notices for reviewed delivery. Record attendance and votes
        against reviewed company rules. Recording a vote creates a draft
        Governance decision for authority review.
      </p>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void write("", {})}>Retry the same save</button>
      )}
      {!building ? (
        <p>Open meetings from a building.</p>
      ) : !model ? (
        <Loading />
      ) : (
        <>
          <form className="panel form" onSubmit={create}>
            <fieldset disabled={busy || !!pending.current}>
              <legend>Prepare a meeting</legend>
              <label>
                Title
                <input name="title" required minLength={3} maxLength={240} />
              </label>
              <label>
                When
                <input name="when" type="datetime-local" required />
              </label>
              <label>
                Published pack
                <select name="pack" required>
                  <option value="">Choose a pack</option>
                  {model.packs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.report_kind} · {formatDateOnly(p.period_start)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Notice
                <textarea name="notice" required minLength={10} />
              </label>
              <label>
                Reviewed company rules and source reference
                <textarea name="rules" required minLength={5} />
              </label>
              <label>
                Quorum
                <input
                  name="quorum"
                  type="number"
                  min={1}
                  max={500}
                  defaultValue={2}
                />
              </label>
              <label>
                Quorum counts
                <select name="basis">
                  <option>Represented members</option>
                  <option>People present</option>
                </select>
              </label>
              <label>
                Required vote percentage (abstentions excluded)
                <input
                  name="majority"
                  type="number"
                  min={50}
                  max={100}
                  defaultValue={50}
                />
              </label>
              <button>Prepare meeting</button>
            </fieldset>
          </form>
          <section className="panel">
            <h2>Meetings</h2>
            {model.items.length === 0 ? (
              <p>No meetings prepared.</p>
            ) : (
              model.items.map((m) => (
                <p key={m.event_id}>
                  <button
                    disabled={busy || !!pending.current}
                    onClick={() => edit(m)}
                  >
                    {m.title}
                  </button>{" "}
                  · {m.stage}
                </p>
              ))
            )}
          </section>
          {selected && (
            <section className="panel form">
              <h2>{selected.title}</h2>
              <p>{selected.notice}</p>
              <p>
                {selected.rules_reference} · Quorum {selected.quorum}{" "}
                {selected.quorum_basis.toLowerCase()} · Required{" "}
                {selected.majority_percent}% of votes cast, with a tie failing.
              </p>
              <fieldset
                disabled={
                  busy || !!pending.current || selected.stage === "Recorded"
                }
              >
                <legend>Attendance, entitlement and proxies</legend>
                {members.map((m, i) => (
                  <section key={m.personId}>
                    <h3>{name(m.personId)}</h3>
                    <label>
                      Entitlement source
                      <input
                        value={m.entitlement}
                        onChange={(e) =>
                          updateMember(i, { entitlement: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      Votes
                      <input
                        type="number"
                        min={0}
                        value={m.votes}
                        onChange={(e) =>
                          updateMember(i, { votes: Number(e.target.value) })
                        }
                      />
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={m.present}
                        onChange={(e) =>
                          updateMember(i, {
                            present: e.target.checked,
                            proxyPersonId: null,
                          })
                        }
                      />
                      Present
                    </label>
                    <label>
                      Proxy
                      <select
                        value={m.proxyPersonId ?? ""}
                        onChange={(e) =>
                          updateMember(i, {
                            proxyPersonId: e.target.value || null,
                            present: false,
                          })
                        }
                      >
                        <option value="">No proxy</option>
                        {model.people
                          .filter((p) => p.id !== m.personId)
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.display_name}
                            </option>
                          ))}
                      </select>
                    </label>
                    <label>
                      Proxy evidence reference
                      <input
                        value={m.proxyEvidence}
                        onChange={(e) =>
                          updateMember(i, { proxyEvidence: e.target.value })
                        }
                      />
                    </label>
                    <button
                      onClick={() =>
                        setMembers((ms) => ms.filter((_, n) => n !== i))
                      }
                    >
                      Remove member from draft
                    </button>
                  </section>
                ))}
                <label>
                  Add member
                  <select
                    value=""
                    onChange={(e) =>
                      e.target.value &&
                      setMembers((ms) => [
                        ...ms,
                        {
                          personId: e.target.value,
                          entitlement: "",
                          votes: 1,
                          present: false,
                          proxyPersonId: null,
                          proxyEvidence: "",
                        },
                      ])
                    }
                  >
                    <option value="">Choose a person</option>
                    {model.people
                      .filter((p) => !members.some((m) => m.personId === p.id))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.display_name}
                        </option>
                      ))}
                  </select>
                </label>
                <h3>Resolutions</h3>
                {resolutions.map((r, i) => (
                  <section key={i}>
                    <label>
                      Resolution title
                      <input
                        value={r.title}
                        onChange={(e) =>
                          setResolutions((rs) =>
                            rs.map((v, n) =>
                              n === i ? { ...v, title: e.target.value } : v,
                            ),
                          )
                        }
                      />
                    </label>
                    <label>
                      Resolution text
                      <textarea
                        value={r.text}
                        onChange={(e) =>
                          setResolutions((rs) =>
                            rs.map((v, n) =>
                              n === i ? { ...v, text: e.target.value } : v,
                            ),
                          )
                        }
                      />
                    </label>
                    <label>
                      Follow-up action if passed
                      <input
                        value={r.actionTitle}
                        onChange={(e) =>
                          setResolutions((rs) =>
                            rs.map((v, n) =>
                              n === i
                                ? { ...v, actionTitle: e.target.value }
                                : v,
                            ),
                          )
                        }
                      />
                    </label>
                    {members
                      .filter(
                        (m) => m.votes > 0 && (m.present || m.proxyPersonId),
                      )
                      .map((m) => (
                        <label key={m.personId}>
                          {name(m.personId)} vote
                          <select
                            value={
                              r.ballots.find((b) => b.personId === m.personId)
                                ?.vote ?? ""
                            }
                            onChange={(e) =>
                              setResolutions((rs) =>
                                rs.map((v, n) =>
                                  n === i
                                    ? {
                                        ...v,
                                        ballots: [
                                          ...v.ballots.filter(
                                            (b) => b.personId !== m.personId,
                                          ),
                                          {
                                            personId: m.personId,
                                            vote: e.target.value as
                                              "For" | "Against" | "Abstain",
                                          },
                                        ],
                                      }
                                    : v,
                                ),
                              )
                            }
                          >
                            <option value="" disabled>
                              Record vote
                            </option>
                            <option>For</option>
                            <option>Against</option>
                            <option>Abstain</option>
                          </select>
                        </label>
                      ))}
                  </section>
                ))}
                <button
                  onClick={() =>
                    setResolutions((rs) => [
                      ...rs,
                      { title: "", text: "", actionTitle: "", ballots: [] },
                    ])
                  }
                >
                  Add resolution
                </button>
                <label>
                  Minutes
                  <textarea
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                  />
                </label>
                {(["save", "notice", "record"] as const).map((command) => (
                  <button
                    key={command}
                    onClick={() =>
                      void write(
                        `/api/alpha/meetings/${selected.event_id}/${command}`,
                        {
                          expectedVersion: selected.version,
                          members,
                          resolutions,
                          minutes,
                        },
                      )
                    }
                  >
                    {command === "save"
                      ? "Save draft"
                      : command === "notice"
                        ? "Retain notice for reviewed delivery"
                        : "Record meeting outcome"}
                  </button>
                ))}
              </fieldset>
              {selected.stage === "Recorded" && (
                <p>
                  Recorded evidence is retained. Open Governance and Actions to
                  review the resulting decisions and follow-up.
                </p>
              )}
            </section>
          )}
        </>
      )}
    </>
  );
}
